import React, { useMemo } from 'react'
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material'
import classNames from 'classnames'
import Fuse from 'fuse.js'
import './combobox.scss'
import { Tags } from './components/tags'
import { Option as OptionComponent } from './components/option'
import OptionsHeader from './components/optionsHeader'
import FieldWrapper, { FieldStatus } from 'uiComponents/input/fieldWrapper'
import { LucideIconName } from 'utils/lucideIcon'
import { FontWeightType } from 'uiComponents/typography'
import { Chevron } from 'uiComponents/chevron'

export interface Option {
    label: string
    value: string
    iconName?: LucideIconName
    eyebrow?: string
    title?: string
    iconTitle?: string
    renderLabel?: React.ReactNode
    secondaryLabel?: string
    disabledReason?: string
}

const findOption = (options: Option[], value: string) => options.find((option) => option.value === value)

const filterOptions = (options: Option[], { inputValue }: { inputValue: string }) => {
    const query = inputValue.trim()
    if (!query) return options

    // If the query exactly matches an option's ID, return only that result
    const idResults = options.filter(
        (option) => option.value === query || option.secondaryLabel?.replace(/^#/, '') === query,
    )
    if (idResults.length === 1) {
        return idResults
    }

    // If not, fuzzy search
    const fuse = new Fuse(options, {
        keys: [
            { name: 'label', weight: 0.6 },
            { name: 'title', weight: 0.3 },
            { name: 'secondaryLabel', weight: 0.05 },
            { name: 'value', weight: 0.05 },
        ],
        threshold: 0.4,
        minMatchCharLength: 2,
    })

    const searchResults = fuse.search(query)

    return searchResults.map((result) => result.item)
}

const useLazyOptions = (options: Option[], searchValue: string) =>
    useMemo(() => (searchValue ? options : options.slice(0, 200)), [options, searchValue])

export interface ComboboxProps
    extends Omit<AutocompleteProps<any, boolean, boolean, boolean>, 'renderInput' | 'onChange' | 'value'> {
    value?: string | string[] | null
    options: Option[]
    className?: string
    multiSelect?: boolean
    showSelectAll?: boolean
    searchPlaceholder?: string
    multilineInput?: boolean
    disabled?: boolean
    error?: string
    onChange?: (value: string | string[] | null) => void
}

export const Combobox: React.FC<ComboboxProps> = ({
    value,
    options,
    className,
    multiSelect,
    showSelectAll,
    searchPlaceholder = 'Search...',
    multilineInput = false,
    disabled,
    error,
    onChange,
    loading,
    limitTags,
    ...props
}) => {
    const selectedValueObjects = useMemo(() => {
        if (loading) {
            return multiSelect ? [{}] : null
        }
        if (Array.isArray(value)) {
            return value.map((value) => findOption(options, value)).filter(Boolean)
        }
        const foundOption = value ? findOption(options, value) : null
        if (multiSelect) {
            return foundOption ? [foundOption] : []
        }
        return foundOption || null
    }, [value, options, multiSelect, loading])

    const [searchValue, setSearchValue] = React.useState('')
    const [isOpen, setIsOpen] = React.useState(false)

    const lazyOptions = useLazyOptions(options, searchValue)

    const handleChange = (_event: React.SyntheticEvent, selectedOption: Option | Option[] | null) => {
        if (!selectedOption) {
            onChange?.(multiSelect ? [] : null)
            return
        }

        if (Array.isArray(selectedOption)) {
            const validOptions = selectedOption.filter(Boolean)
            onChange?.(validOptions.map((option) => option.value))
        } else {
            onChange?.(selectedOption.value)
        }
        if (multiSelect) {
            setSearchValue(searchValue)
        }
    }

    const handleInputChange = (event: any, value: string) => {
        setSearchValue(value)
    }

    const handleClose = () => {
        setSearchValue('')
        setIsOpen(false)
    }

    const handleOpen = () => {
        setIsOpen(true)
    }

    const filteredOptions = useMemo(() => {
        if (!searchValue) return lazyOptions
        return filterOptions(lazyOptions, { inputValue: searchValue })
    }, [lazyOptions, searchValue])

    const selectedCount = Array.isArray(selectedValueObjects) ? selectedValueObjects.length : 0

    const allFilteredOptionsSelected = useMemo(() => {
        if (!Array.isArray(value) || value.length === 0) return false
        const selectedValues = new Set(value)
        return filteredOptions.every((option: Option) => selectedValues.has(option.value))
    }, [value, filteredOptions])

    const handleSelectAll = () => {
        const currentValues = new Set(value ?? [])
        const valuesToAdd = filteredOptions.map((option: Option) => option.value)
        valuesToAdd.forEach((val: string) => currentValues.add(val))
        onChange?.(Array.from(currentValues))
    }

    const handleDeselectAll = () => {
        const filteredValues = new Set(filteredOptions.map((option: Option) => option.value))
        const remainingValues = Array.isArray(value) ? value.filter((val: string) => !filteredValues.has(val)) : []
        onChange?.(remainingValues)
    }

    const optionsHeaderVisible = showSelectAll && multiSelect && filteredOptions.length > 0

    return (
        <Autocomplete
            className={classNames('combobox', className, {
                loading,
                disabled,
                error,
                optionsHeaderVisible,
            })}
            multiple={multiSelect}
            options={lazyOptions}
            filterOptions={filterOptions}
            inputValue={searchValue}
            value={selectedValueObjects}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onClose={handleClose}
            onOpen={handleOpen}
            disabled={loading || disabled}
            noOptionsText="No options"
            popupIcon={<Chevron direction={isOpen ? 'up' : 'down'} size={12} strokeWidth={1.5} />}
            renderInput={(params) => (
                <>
                    <TextField
                        {...params}
                        placeholder={loading ? 'Loading...' : searchPlaceholder}
                        multiline={multilineInput}
                    />
                    <OptionsHeader
                        filteredOptionsCount={filteredOptions.length}
                        totalOptionsCount={options.length}
                        selectedCount={selectedCount}
                        allFilteredOptionsSelected={allFilteredOptionsSelected}
                        onSelectAll={handleSelectAll}
                        onDeselectAll={handleDeselectAll}
                    />
                </>
            )}
            renderTags={(value, getTagProps) => (
                <Tags
                    value={value?.filter(Boolean) || []}
                    loading={loading}
                    getTagProps={getTagProps}
                    limitTags={limitTags}
                />
            )}
            renderOption={(props, option, { selected }) => {
                if (!option) return null
                return <OptionComponent key={option.value} props={props} option={option} selected={selected} />
            }}
            getOptionLabel={(option) => option?.label || ''}
            getOptionDisabled={(option) => !!option?.disabledReason}
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: 'flip',
                            enabled: false,
                        },
                        {
                            name: 'offset',
                            options: {
                                offset: [0, optionsHeaderVisible ? 33 : 0],
                            },
                        },
                    ],
                },
            }}
            {...props}
        />
    )
}

interface MultiSelectComboboxProps extends Omit<ComboboxProps, 'multiSelect'> {
    value?: string[] | null
}

export const MultiSelectCombobox: React.FC<MultiSelectComboboxProps> = (props) => (
    <Combobox multiSelect disableCloseOnSelect showSelectAll limitTags={10} clearOnBlur={false} {...props} />
)

export interface ComboboxFieldProps extends ComboboxProps {
    label?: string
    status?: FieldStatus
    icon?: LucideIconName
    fontWeight?: FontWeightType
}

export const ComboboxField: React.FC<ComboboxFieldProps> = ({
    id,
    label,
    status = 'normal',
    icon,
    fontWeight,
    className,
    error,
    ...props
}) => (
    <FieldWrapper
        id={id}
        label={label}
        status={status}
        icon={icon}
        fontWeight={fontWeight}
        className={className}
        error={error}
    >
        <Combobox id={id} error={error} {...props} />
    </FieldWrapper>
)

export type MultiSelectComboboxFieldProps = MultiSelectComboboxProps &
    Omit<ComboboxFieldProps, 'value' | 'multiSelect'> & {
        activateOnClick?: boolean
        autoActivate?: boolean
        onDeactivate?: () => void
    }

export const MultiSelectComboboxField: React.FC<MultiSelectComboboxFieldProps> = ({
    id,
    label,
    status = 'normal',
    icon,
    fontWeight,
    className,
    error,
    activateOnClick,
    autoActivate,
    onDeactivate,
    value,
    options,
    limitTags = 10,
    ...props
}) => {
    const [isActive, setIsActive] = React.useState(!!autoActivate)
    const wrapperRef = React.useRef<HTMLDivElement>(null)
    const onDeactivateRef = React.useRef(onDeactivate)
    onDeactivateRef.current = onDeactivate

    React.useEffect(() => {
        if (autoActivate) {
            setIsActive(true)
        }
    }, [autoActivate])

    React.useEffect(() => {
        if (isActive && wrapperRef.current) {
            const input = wrapperRef.current.querySelector<HTMLInputElement>('.MuiAutocomplete-input')
            if (input) {
                input.focus()
            }
        }
    }, [isActive])

    const deactivate = React.useCallback(() => {
        setIsActive(false)
        onDeactivateRef.current?.()
    }, [])

    React.useEffect(() => {
        if (!activateOnClick || !isActive) return

        const handleMouseDown = (e: MouseEvent) => {
            const target = e.target as Node
            if (wrapperRef.current?.contains(target)) return
            const poppers = Array.from(document.querySelectorAll('.MuiAutocomplete-popper'))
            if (poppers.some((popper) => popper.contains(target))) return
            deactivate()
        }

        document.addEventListener('mousedown', handleMouseDown)
        return () => document.removeEventListener('mousedown', handleMouseDown)
    }, [activateOnClick, isActive, deactivate])

    const fieldWrapperProps = { id, label, status, icon, fontWeight, className, error }

    if (activateOnClick && !isActive) {
        const selectedOptions = value ? options.filter((opt) => value.includes(opt.value)) : []

        return (
            <FieldWrapper {...fieldWrapperProps}>
                <div
                    ref={wrapperRef}
                    className="combobox-display"
                    onClick={() => setIsActive(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setIsActive(true)}
                >
                    <Tags value={selectedOptions} limitTags={limitTags} readOnly />
                </div>
            </FieldWrapper>
        )
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' && activateOnClick) {
            deactivate()
        }
    }

    return (
        <FieldWrapper {...fieldWrapperProps}>
            <div ref={wrapperRef} onKeyDown={handleKeyDown}>
                <MultiSelectCombobox
                    id={id}
                    error={error}
                    value={value}
                    options={options}
                    limitTags={limitTags}
                    {...props}
                />
            </div>
        </FieldWrapper>
    )
}
